import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createAxiosInstance } from 'src/utils/axios';
import { purchaseOrderNoteTypes } from 'src/components/PurchaseOrderDetails/constants/purchaseOrderNoteTypes';
import PurchaseOrderNoteService from 'src/services/PurchaseOrderNoteService';
import DispatchNoteService from 'src/services/DispatchNoteService';

const purchaseOrderNoteService = new PurchaseOrderNoteService(
  createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);
const dispatchNoteService = new DispatchNoteService(
  createAxiosInstance(process.env.REACT_APP_DISPATCH_QORE_API)
);


/**
 * Custom hook to get purchase order notes from Cash Qore API.
 * 
 * @param {number} poNumber
 * @param {number} dispatchId
 *
 * @returns {{
 *   notes: {
 *    noteId: number,
 *    noteTypeId: number,
 *    noteTypeName: string,
 *    source: string,
 *    sourceId: number,
 *    PurchaseOrderNoteTypeId: number,
 *    text: string,
 *    dateCreated: Date,
 *    createdByEmail: string,
 *   }[], isLoading: boolean }} Object containing the purchase order notes and loading state.
 */
const useFetchPurchaseOrderNotes = (poNumber, dispatchId) => {
  const { enqueueSnackbar } = useSnackbar();
  const [results, setResults] = useState([]);
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [params, setParams] = useState({
    orderBy: 'DateCreated',
    order: 'desc',
  });

  const refresh = async () => {
    await fetchPurchaseOrderNotes();
  };

  const fetchPurchaseOrderNotes = async () => {
    setIsLoading(true);
    let result = [];

    const purchaseOrderNotes = purchaseOrderNoteService.fetchPurchaseOrderNotes(dispatchId);
    const dispatchNotes = dispatchNoteService.fetchDispatchNotes(dispatchId);

    try {
      await Promise.allSettled([purchaseOrderNotes, dispatchNotes])
        .then((values) => {
          if (values[0].status === 'fulfilled') {
            result.push(...values[0].value.data.map(value => {
              return {
                noteId: value.PurchaseOrderNoteId,
                noteTypeId: value.PurchaseOrderNoteTypeId,
                noteTypeName: value.NoteTypeName,
                source: 'Purchase Order',
                sourceId: value.PoNumber,
                text: value.NoteText,
                dateCreated: value.DateCreated,
                createdByEmail: value.CreatedBy,
              };
            }));
          }

          if (values[1].status === 'fulfilled') {
            const dispatchNotes = [...values[1].value.data.providerNotes, ...values[1].value.data.internalNotes];
            result.push(...dispatchNotes.map(value => {
              return {
                noteId: value.noteId,
                noteTypeId: value.noteTypeId,
                source: 'Dispatch',
                sourceId: value.dispatchId,
                noteTypeName: purchaseOrderNoteTypes?.find(type => Number(type.value) === value.noteTypeId)?.label ?? 'Unknown',
                text: value.content,
                dateCreated: value.dateCreated,
                createdByEmail: value.createdByEmail,
              };
            }));
          }
        });

      setResults(result);
      setNotes(result);
      setTotal(result?.length ?? 0);
    } catch (error) {
      enqueueSnackbar(`Failed fetching notes. ${error?.message}`, {
        variant: 'error',
        preventDuplicate: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggles the orderBy state, allows sorting by a single column.
   * @param {string} column
   */
  const handleOrderBy = (column) => {
    const order = column === params.orderBy ? (params.order === 'asc' ? 'desc' : 'asc') : 'desc';
    setParams({ order: order, orderBy: column });
  };

  useEffect(() => {
    if (dispatchId && poNumber)
      fetchPurchaseOrderNotes();
  }, [dispatchId, poNumber]);

  useEffect(() => {
    if (params.orderBy === 'DateCreated') {
      if (params.order === 'asc') {
        setNotes(results?.concat()?.sort((first, second) => new Date(first.dateCreated).getTime() - new Date(second.dateCreated).getTime()));
      } else {
        setNotes(results?.concat()?.sort((first, second) => new Date(second.dateCreated).getTime() - new Date(first.dateCreated).getTime()));
      }
    }
  }, [params, results]);

  return {
    notes,
    total,
    isLoading,
    handleOrderBy,
    params,
    setParams,
    refresh,
  };
};

useFetchPurchaseOrderNotes.propTypes = {
  poNumber: PropTypes.string.isRequired,
  dispatchId: PropTypes.string.isRequired,
};


export default useFetchPurchaseOrderNotes;
